import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { mdiClipboardText } from "@mdi/js";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { MiIcon } from "../../MiIcon/MiIcon";

const EventIntervalSlots = [0, 1, 2, 3];

const InactiveSlot = ({
  adjustedHeight,
  deactivatedIntervals,
  classes,
  cx,
  theme,
}: {
  adjustedHeight: number;
  deactivatedIntervals: { slots: number[]; note: string };
  classes: ReturnType<typeof useStyles>["classes"];
  cx: ReturnType<typeof useStyles>["cx"];
  theme: ReturnType<typeof useStyles>["theme"];
}) => {
  return (
    <Box className={cx([classes.item, classes.inactiveItem])}>
      {EventIntervalSlots.map((interval, index) => {
        if (!deactivatedIntervals.slots.includes(interval)) {
          return (
            <Box
              key={interval}
              style={{
                height: adjustedHeight * 0.25,
                backgroundColor: theme.palette.blue.light,
              }}
            />
          );
        }
        return (
          <Box
            key={interval}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              padding: theme.spacing(1),
              height: adjustedHeight * 0.25,
            }}
            data-test-id={`inactive-event-slot-${index}`}
          >
            {index ===
              deactivatedIntervals.slots[
                deactivatedIntervals.slots.length - 1
              ] && (
              <Tooltip
                title={
                  <Typography fontSize={theme.fontSize.xsmall}>
                    Note
                    <br />
                    {deactivatedIntervals.note}
                  </Typography>
                }
                slotProps={{
                  tooltip: {
                    sx: {
                      p: 1,
                      backgroundColor: `${theme.palette.common.black}CC`,
                    },
                  },
                }}
              >
                <Box zIndex={1}>
                  <MiIcon
                    path={mdiClipboardText}
                    color={theme.palette.info.main}
                    size={0.5}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const EventSlot = memo(
  ({
    adjustedHeight,
    deactivatedIntervals,
  }: Readonly<{
    adjustedHeight: number;
    deactivatedIntervals?: { slots: number[]; note: string };
  }>) => {
    const showInactive = (deactivatedIntervals?.slots?.length ?? 0) > 0;
    const { classes, cx, theme } = useStyles({
      adjustedHeight,
    });

    return (
      <Grid data-testid="event-slot">
        {showInactive && deactivatedIntervals ? (
          <InactiveSlot
            adjustedHeight={adjustedHeight}
            deactivatedIntervals={deactivatedIntervals}
            classes={classes}
            cx={cx}
            theme={theme}
          />
        ) : (
          <Box className={classes.item} />
        )}
      </Grid>
    );
  }
);

const useStyles = makeStyles<{ adjustedHeight: number }>()(
  (theme, { adjustedHeight }) => ({
    item: {
      backgroundColor: "transparent",
      height: adjustedHeight,
      boxShadow: "none",
      borderTop: `0.5px solid ${theme.palette.common.white}`,
      borderBottom: `0.5px solid ${theme.palette.common.white}`,
    },
    inactiveItem: {
      backgroundImage: `repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.12) 0px, rgba(0, 0, 0, 0.12) 2px, transparent 2px, transparent 6px)`,
    },
  })
);
